import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReportingService } from '../_service/reporting.service';
import Swal from 'sweetalert2';
import { NgbModule, NgbCalendar, NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

interface Transaction {
  id: number;
  amount: number;
  bank: string;
  date: Date;
  status: string;
}

interface Bank {
  id: number;
  name: string;
}

@Component({
  selector: 'app-wallet',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbModule],
  templateUrl: './wallet.component.html',
  styleUrl: './wallet.component.scss'
})
export class WalletComponent {
  private reportingSrvs = inject(ReportingService);
  private calendar = inject(NgbCalendar);

  balance: any;
  unAppBalance: any;
  showWithdrawModal: boolean = false;
  selectedBank: string = '';
  withdrawAmount: number = 0;
  page = 1;
  size = 0;
  pageSize = 15;
  banks: any;
  withdrawalHistory: any;

  // Loading states
  isLoadingBalance = false;
  isLoadingUnappBalance = false;
  isLoadingHistory = false;
  isLoadingBanks = false;
  isSubmittingWithdrawal = false;

  // Filter states
  isFiltersCollapsed = true;
  showDatePicker = false;
  selectedDateFilter: 'today' | 'week' | 'month' | 'custom' | undefined = undefined;
  dateRange: { from: NgbDate | null; to: NgbDate | null } = {
    from: null,
    to: null
  };
  hoveredDate: NgbDate | null = null;

  transactions: Transaction[] = [
    { id: 1, amount: 1000, bank: 'Bank A', date: new Date(), status: 'Completed' },
    { id: 2, amount: 500, bank: 'Bank B', date: new Date(), status: 'Pending' }
  ];
  isWalletBalanceHidden: boolean = false;
  isUnappliedBalanceHidden: boolean = false;

  ngOnInit(): void {
    // Load balance visibility preferences from localStorage
    this.isWalletBalanceHidden = localStorage.getItem('hideWalletBalance') === 'true';
    this.isUnappliedBalanceHidden = localStorage.getItem('hideUnappliedBalance') === 'true';
    this.getWalletBalance();
    this.getUnAppWalletBalance();
    this.fetchWithdrawalHistory();
    this.fetchBanks();
  }

  fetchWithdrawalHistory() {
    this.isLoadingHistory = true;
    const params: any = { page: this.page };

    // Add date parameters if both dates are selected
    if (this.dateRange.from && this.dateRange.to) {
      params.start_date = this.formatDateForApi(this.dateRange.from);
      params.end_date = this.formatDateForApi(this.dateRange.to);
    }

    console.log('Fetching withdrawal history with params:', params);

    this.reportingSrvs.fetchWithdrawal(params).subscribe({
      next: (res: any) => {
        this.withdrawalHistory = res.data;
        this.size = res.total;
        this.pageSize = res.per_page;
        this.isLoadingHistory = false;
        console.log('Withdrawal History', res);
      },
      error: (err: any) => {
        console.log(err);
        this.isLoadingHistory = false;
      }
    });
  }

  // Date picker methods
  onDateSelection(date: NgbDate) {
    if (!this.dateRange.from && !this.dateRange.to) {
      this.dateRange.from = date;
    } else if (this.dateRange.from && !this.dateRange.to && date.after(this.dateRange.from)) {
      this.dateRange.to = date;
      this.applyDateFilter();
    } else {
      this.dateRange.to = null;
      this.dateRange.from = date;
    }
  }

  isHovered(date: NgbDate) {
    return this.dateRange.from && !this.dateRange.to && this.hoveredDate && date.after(this.dateRange.from) && date.before(this.hoveredDate);
  }

  isInside(date: NgbDate) {
    return this.dateRange.from && this.dateRange.to && date.after(this.dateRange.from) && date.before(this.dateRange.to);
  }

  isRange(date: NgbDate) {
    return date.equals(this.dateRange.from) || (this.dateRange.to && date.equals(this.dateRange.to)) || this.isInside(date) || this.isHovered(date);
  }

  formatDateForApi(date: NgbDate | null): string | undefined {
    if (!date) return undefined;
    return `${date.year}-${date.month.toString().padStart(2, '0')}-${date.day.toString().padStart(2, '0')}`;
  }

  formatDisplayDate(date: NgbDate | null): string {
    if (!date) return '';
    return new Date(date.year, date.month - 1, date.day).toLocaleDateString();
  }

  applyDateFilter() {
    this.showDatePicker = false;
    this.page = 1; // Reset to first page
    this.fetchWithdrawalHistory();
  }

  setDateRange(range: string) {
    console.log('Setting date range:', range);
    const today = this.calendar.getToday();
    switch (range) {
      case 'today':
        this.dateRange = { from: today, to: today };
        this.selectedDateFilter = 'today';
        break;
      case 'week':
        this.dateRange = {
          from: this.calendar.getPrev(today, 'd', 7),
          to: today
        };
        this.selectedDateFilter = 'week';
        break;
      case 'month':
        this.dateRange = {
          from: this.calendar.getPrev(today, 'm', 1),
          to: today
        };
        this.selectedDateFilter = 'month';
        break;
    }
    console.log('Date range set to:', this.dateRange);
    this.page = 1; // Reset to first page
    this.fetchWithdrawalHistory();
  }

  onCustomDateSelect(startDate: string, endDate: string) {
    console.log('Custom date select:', startDate, endDate);
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      this.dateRange = {
        from: new NgbDate(start.getFullYear(), start.getMonth() + 1, start.getDate()),
        to: new NgbDate(end.getFullYear(), end.getMonth() + 1, end.getDate())
      };

      console.log('Custom date range set:', this.dateRange);
      this.selectedDateFilter = 'custom';
      this.showDatePicker = false;
      this.page = 1; // Reset to first page
      this.fetchWithdrawalHistory();
    }
  }

  toggleFiltersCollapse() {
    this.isFiltersCollapsed = !this.isFiltersCollapsed;
  }

  clearFilters() {
    console.log('Clearing filters');
    this.dateRange = { from: null, to: null };
    this.selectedDateFilter = undefined;
    this.showDatePicker = false;
    this.page = 1;
    this.fetchWithdrawalHistory();
  }
  toggleWalletBalanceVisibility() {
    this.isWalletBalanceHidden = !this.isWalletBalanceHidden;
    localStorage.setItem('hideWalletBalance', this.isWalletBalanceHidden.toString());
  }
  refresh(event: any) {
    console.log('Pagination event:', event, typeof event);
    // Handle both direct page numbers and event objects
    const pageNumber = typeof event === 'object' ? event.page || event : event;
    console.log('Extracted page number:', pageNumber);
    this.page = pageNumber;
    this.fetchWithdrawalHistory();
  }

  onPageChange(page: number) {
    console.log('Page change to:', page, typeof page);
    this.page = page;
    this.fetchWithdrawalHistory();
  }
  toggleUnappliedBalanceVisibility() {
    this.isUnappliedBalanceHidden = !this.isUnappliedBalanceHidden;
    localStorage.setItem('hideUnappliedBalance', this.isUnappliedBalanceHidden.toString());
  }
  openWithdrawModal() {
    this.showWithdrawModal = true;
  }
  getWalletBalance() {
    this.isLoadingBalance = true;
    this.reportingSrvs.getWalletBalance().subscribe({
      next: (res: any) => {
        this.balance = res;
        this.isLoadingBalance = false;
      },
      error: (error) => {
        console.error('Error fetching wallet balance:', error);
        this.isLoadingBalance = false;
      }
    });
  }

  getUnAppWalletBalance() {
    this.isLoadingUnappBalance = true;
    this.reportingSrvs.getUnAppWalletBalance().subscribe({
      next: (res: any) => {
        this.unAppBalance = res;
        this.isLoadingUnappBalance = false;
      },
      error: (error) => {
        console.error('Error fetching unapplied balance:', error);
        this.isLoadingUnappBalance = false;
      }
    });
  }

  fetchBanks() {
    this.isLoadingBanks = true;
    this.reportingSrvs.getBanks().subscribe({
      next: (res: any) => {
        this.banks = res;
        this.isLoadingBanks = false;
      },
      error: (error) => {
        console.error('Error fetching banks:', error);
        this.isLoadingBanks = false;
      }
    });
  }

  submitWithdrawal() {
    if (this.withdrawAmount > 0 && this.selectedBank) {
      this.isSubmittingWithdrawal = true;
      let payload = {
        amount: this.withdrawAmount,
        bank_id: this.selectedBank
      };

      this.reportingSrvs.submitWithdrawal(payload).subscribe({
        next: (res: any) => {
          this.isSubmittingWithdrawal = false;
          this.closeWithdrawModal();
          // Refresh withdrawal history and balances
          this.fetchWithdrawalHistory();
          this.getWalletBalance();
          Swal.fire({
            title: 'Success!',
            text: 'Withdrawal submitted successfully.',
            icon: 'success',
            confirmButtonText: 'OK'
          });
        },
        error: (error) => {
          console.error('Error submitting:', error);
          this.isSubmittingWithdrawal = false;
          Swal.fire({
            title: 'Error!',
            text: error.response?.error?.message || 'An error occurred while processing your withdrawal.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      });
    }
  }

  closeWithdrawModal() {
    this.showWithdrawModal = false;
    this.selectedBank = '';
    this.withdrawAmount = 0;
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      completed: 'status-success',
      success: 'status-success',
      pending: 'status-warning',
      processing: 'status-warning',
      failed: 'status-danger',
      cancelled: 'status-secondary',
      rejected: 'status-danger'
    };
    return statusClasses[status.toLowerCase()] || 'status-secondary';
  }
}
